import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, getUser } from "../api";
import { toast } from "sonner";
import { Trash2, X } from "lucide-react";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k${Math.random().toString(36).slice(2)}`);
const mkRow = (w = {}) => ({ weekday: 1, start: "09:00", end: "13:00", ...w, _key: uid() });
const mkIp = (w = {}) => ({ weekday: 1, start: "09:00", end: "13:00", location_kind: "free", location_label: "", org_id: null, ...w, _key: uid() });

export default function Agenda() {
  const { t } = useTranslation();
  const u = getUser();
  const [mode, setMode] = useState("video");
  // Video (invariato)
  const [weekly, setWeekly] = useState(() => [mkRow({ start: "09:00", end: "13:00" }), mkRow({ start: "14:00", end: "18:00" })]);
  const [price, setPrice] = useState("");
  const [loaded, setLoaded] = useState(false);
  // In presenza
  const [ipWeekly, setIpWeekly] = useState(() => [mkIp()]);
  const [ipDur, setIpDur] = useState(30);
  const [ipPrice, setIpPrice] = useState("");
  const [ipReminder, setIpReminder] = useState(0);
  const [orgQuery, setOrgQuery] = useState({}); // key -> query string
  const [orgResults, setOrgResults] = useState({}); // key -> [orgs]

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      if (!data) return;
      api.get(`/professionals/${data.id}/availability`).then(({ data: a }) => { if (a?.weekly?.length) setWeekly(a.weekly.map(mkRow)); });
      api.get(`/professionals/${data.id}`).then(({ data: pr }) => {
        const pc = pr?.profile?.price_cents; if (pc != null && pc > 0) setPrice(String(pc / 100)); setLoaded(true);
      }).catch(() => setLoaded(true));
      api.get(`/professionals/${data.id}/in-person-availability`).then(({ data: a }) => {
        if (a?.weekly?.length) setIpWeekly(a.weekly.map(mkIp));
        if (a?.duration_minutes) setIpDur(a.duration_minutes);
        if (a?.reminder_minutes != null) setIpReminder(a.reminder_minutes);
        if (a?.price_cents != null && a.price_cents > 0) setIpPrice(String(a.price_cents / 100));
      });
    });
  }, []);

  const saveVideo = async () => {
    try {
      await api.put("/me/availability", { weekly: weekly.map(({ _key, ...w }) => w), exceptions: [] });
      const euros = price === "" ? 0 : Math.round(parseFloat(price) * 100);
      if (price !== "" && (isNaN(euros) || euros < 0)) { toast.error(t("events.error_generic")); return; }
      await api.put("/me/professional-profile", { price_cents: euros });
      toast.success(t("agenda.saved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };

  const saveIp = async () => {
    const euros = ipPrice === "" ? 0 : Math.round(parseFloat(ipPrice) * 100);
    if (!(euros > 0)) { toast.error(t("inperson.err_price")); return; }
    for (const r of ipWeekly) {
      if (r.location_kind === "org" && !r.org_id) { toast.error(t("inperson.err_org")); return; }
      if (r.location_kind === "free" && !(r.location_label || "").trim()) { toast.error(t("inperson.err_loc")); return; }
    }
    try {
      await api.put("/me/in-person-availability", {
        duration_minutes: Number(ipDur), price_cents: euros, reminder_minutes: Number(ipReminder),
        weekly: ipWeekly.map(({ _key, ...w }) => w), exceptions: [],
      });
      toast.success(t("agenda.saved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };

  const searchOrg = async (key, q) => {
    setOrgQuery({ ...orgQuery, [key]: q });
    if (!q || q.length < 1) { setOrgResults({ ...orgResults, [key]: [] }); return; }
    try { const { data } = await api.get(`/organizations-search?q=${encodeURIComponent(q)}`); setOrgResults({ ...orgResults, [key]: data }); }
    catch { setOrgResults({ ...orgResults, [key]: [] }); }
  };
  const pickOrg = (i, org) => { const n = [...ipWeekly]; n[i] = { ...n[i], location_kind: "org", org_id: org.id, location_label: org.org_name }; setIpWeekly(n); setOrgResults({ ...orgResults, [n[i]._key]: [] }); setOrgQuery({ ...orgQuery, [n[i]._key]: "" }); };
  const clearOrg = (i) => { const n = [...ipWeekly]; n[i] = { ...n[i], location_kind: "free", org_id: null, location_label: "" }; setIpWeekly(n); };
  const invite = async () => {
    try { const { data } = await api.post("/pro/invite-organization"); await navigator.clipboard?.writeText(data.invite_url); toast.success(t("inperson.invite_done")); }
    catch { toast.error(t("events.error_generic")); }
  };

  const days = t("agenda.days").split(",");
  if (u?.role !== "professional") return <Layout><div className="yp-container max-w-2xl py-8" data-testid="agenda-forbidden">{t("agenda_guard")}</div></Layout>;

  return (<Layout><div className="yp-container max-w-2xl py-8">
    <h1 className="text-3xl font-bold mb-6">{t("agenda_title")}</h1>
    <div className="flex gap-2 mb-6" role="tablist" data-testid="agenda-tabs">
      {[["video", "inperson.tab_video"], ["inperson", "inperson.tab_inperson"]].map(([k, lbl]) => (
        <button key={k} role="tab" aria-selected={mode === k} onClick={() => setMode(k)}
          className={`yp-btn !py-1.5 !px-4 text-sm ${mode === k ? "yp-btn-primary" : "yp-btn-outline"}`}
          data-testid={`agenda-tab-${k}`}>{t(lbl)}</button>))}
    </div>

    {mode === "video" && (
      <div className="yp-card p-6 space-y-4" data-testid="agenda-video-section">
        <div><label className="text-sm font-medium" htmlFor="agenda-price">{t("agenda.price_label")}</label>
          <input id="agenda-price" type="number" step="0.01" min="0" className="yp-input mt-1" value={price}
            onChange={e => setPrice(e.target.value)} data-testid="agenda-price" disabled={!loaded} /></div>
        <div><label className="text-sm font-medium">{t("agenda.weekly")}</label>
          {weekly.map((w, i) => (
            <div key={w._key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 mt-2 items-center">
              <select className="yp-input" value={w.weekday} onChange={e => { const n = [...weekly]; n[i].weekday = Number(e.target.value); setWeekly(n); }} data-testid={`agenda-day-${i}`}>
                {days.map((d, idx) => <option key={idx} value={idx} label={d} />)}
              </select>
              <input type="time" className="yp-input" value={w.start} onChange={e => { const n = [...weekly]; n[i].start = e.target.value; setWeekly(n); }} data-testid={`agenda-start-${i}`} />
              <input type="time" className="yp-input" value={w.end} onChange={e => { const n = [...weekly]; n[i].end = e.target.value; setWeekly(n); }} data-testid={`agenda-end-${i}`} />
              <button type="button" onClick={() => setWeekly(weekly.filter(x => x._key !== w._key))} aria-label={t("agenda.remove_slot")}
                className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md text-red-600 hover:bg-red-50" data-testid={`agenda-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>))}
          <button className="yp-btn yp-btn-ghost mt-2 !text-xs" onClick={() => setWeekly([...weekly, mkRow({ weekday: 2 })])} data-testid="agenda-add">+ {t("agenda.add_slot")}</button>
        </div>
        <button className="yp-btn yp-btn-primary w-full" onClick={saveVideo} data-testid="agenda-save">{t("common.save")}</button>
      </div>)}

    {mode === "inperson" && (
      <div className="yp-card p-6 space-y-4" data-testid="agenda-inperson-section">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm font-medium">{t("inperson.duration")}</label>
            <select className="yp-input mt-1" value={ipDur} onChange={e => setIpDur(Number(e.target.value))} data-testid="ip-duration">
              {[15, 20, 30, 45, 60].map(d => <option key={d} value={d} label={`${d} min`} />)}
            </select></div>
          <div><label className="text-sm font-medium">{t("inperson.price")}</label>
            <input type="number" step="0.01" min="0" className="yp-input mt-1" value={ipPrice} onChange={e => setIpPrice(e.target.value)} data-testid="ip-price" /></div>
        </div>
        <div><label className="text-sm font-medium">{t("inperson.reminder")}</label>
          <select className="yp-input mt-1" value={ipReminder} onChange={e => setIpReminder(Number(e.target.value))} data-testid="ip-reminder">
            {[0, 30, 60, 120, 180, 360, 720, 1440, 2880].map(m => <option key={m} value={m} label={m === 0 ? t("inperson.reminder_off") : t(`inperson.rem_${m}`)} />)}
          </select></div>
        <div><label className="text-sm font-medium">{t("agenda.weekly")}</label>
          {ipWeekly.map((w, i) => (
            <div key={w._key} className="border border-slate-200 rounded-lg p-3 mt-2 space-y-2" data-testid={`ip-row-${i}`}>
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-center">
                <select className="yp-input" value={w.weekday} onChange={e => { const n = [...ipWeekly]; n[i].weekday = Number(e.target.value); setIpWeekly(n); }} data-testid={`ip-day-${i}`}>
                  {days.map((d, idx) => <option key={idx} value={idx} label={d} />)}
                </select>
                <input type="time" className="yp-input" value={w.start} onChange={e => { const n = [...ipWeekly]; n[i].start = e.target.value; setIpWeekly(n); }} data-testid={`ip-start-${i}`} />
                <input type="time" className="yp-input" value={w.end} onChange={e => { const n = [...ipWeekly]; n[i].end = e.target.value; setIpWeekly(n); }} data-testid={`ip-end-${i}`} />
                <button type="button" onClick={() => setIpWeekly(ipWeekly.filter(x => x._key !== w._key))} aria-label={t("agenda.remove_slot")}
                  className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md text-red-600 hover:bg-red-50" data-testid={`ip-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              {w.location_kind === "org" && w.org_id ? (
                <div className="flex items-center gap-2 text-sm" data-testid={`ip-org-selected-${i}`}>
                  <span className="yp-badge yp-badge-approved">@{w.location_label}</span>
                  <button type="button" className="text-slate-500 hover:text-red-600" onClick={() => clearOrg(i)} data-testid={`ip-org-clear-${i}`}><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="relative">
                  <input className="yp-input" value={w.location_label} placeholder={t("inperson.loc_placeholder")}
                    onChange={e => {
                      const v = e.target.value; const n = [...ipWeekly]; n[i].location_label = v; n[i].location_kind = "free"; n[i].org_id = null; setIpWeekly(n);
                      if (v.startsWith("@")) searchOrg(w._key, v.slice(1)); else setOrgResults({ ...orgResults, [w._key]: [] });
                    }} data-testid={`ip-loc-${i}`} />
                  {(orgResults[w._key] || []).length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow max-h-48 overflow-auto" data-testid={`ip-org-results-${i}`}>
                      {(orgResults[w._key] || []).map(o => (
                        <button key={o.id} type="button" className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50" onClick={() => pickOrg(i, o)} data-testid={`ip-org-opt-${o.id}`}>{o.org_name}</button>))}
                    </div>)}
                  {(w.location_label || "").startsWith("@") && (orgResults[w._key] || []).length === 0 && (
                    <button type="button" className="yp-btn yp-btn-outline !py-1 !px-3 text-xs mt-2" onClick={invite} data-testid={`ip-invite-${i}`}>{t("inperson.invite_org")}</button>)}
                </div>
              )}
            </div>))}
          <button className="yp-btn yp-btn-ghost mt-2 !text-xs" onClick={() => setIpWeekly([...ipWeekly, mkIp({ weekday: 2 })])} data-testid="ip-add">+ {t("agenda.add_slot")}</button>
        </div>
        <button className="yp-btn yp-btn-primary w-full" onClick={saveIp} data-testid="ip-save">{t("common.save")}</button>
      </div>)}
  </div></Layout>);
}
